// Company time is Italian time (Europe/Rome), whatever the phone's time zone:
// "08:30" in a forgotten-punch request means 08:30 in Italy, and the app shows
// the same clock as the HR portal. A phone left on another zone (a worker back
// from abroad, a wrong setting) would otherwise send and show shifted times.

export const COMPANY_TIME_ZONE = 'Europe/Rome';

const parts = new Intl.DateTimeFormat('en-GB', {
  timeZone: COMPANY_TIME_ZONE,
  hourCycle: 'h23',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
});

/** Minutes Europe/Rome is ahead of UTC at the given instant (60 or 120). */
export function romeOffsetMinutes(ms: number): number {
  const p = Object.fromEntries(parts.formatToParts(new Date(ms)).map((x) => [x.type, x.value]));
  const wall = Date.UTC(Number(p.year), Number(p.month) - 1, Number(p.day), Number(p.hour), Number(p.minute), Number(p.second));
  return Math.round((wall - Math.floor(ms / 1000) * 1000) / 60_000);
}

export type RomeOccurrence = 'earlier' | 'later';
export class RomeTimeError extends Error {
  constructor(readonly code: 'invalid' | 'nonexistent' | 'ambiguous') { super(`rome_time_${code}`); }
}

/** All matching instants, in chronological order. Never normalize an invalid civil time. */
export function romeWallTimeCandidates(date: string, hour: number, minute: number): string[] {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || date < '1900-01-01' || date > '2100-12-31'
    || !Number.isInteger(hour) || hour < 0 || hour > 23 || !Number.isInteger(minute) || minute < 0 || minute > 59) throw new RomeTimeError('invalid');
  const [y, m, d] = date.split('-').map(Number);
  const asIfUtc = Date.UTC(y!, m! - 1, d!, hour, minute);
  if (new Date(asIfUtc).toISOString().slice(0, 10) !== date) throw new RomeTimeError('invalid');
  // Capture the offsets on both sides of an Italian clock change, then verify each inverse.
  const offsets = new Set([-86400000, 0, 86400000].map(delta => romeOffsetMinutes(asIfUtc + delta)));
  return [...offsets].map(offset => asIfUtc - offset * 60_000)
    .filter(instant => instant + romeOffsetMinutes(instant) * 60_000 === asIfUtc)
    .sort((a, b) => a - b).map(instant => new Date(instant).toISOString());
}

export function romeWallTimeToIso(date: string, hour: number, minute: number, occurrence?: RomeOccurrence): string {
  const matches = romeWallTimeCandidates(date, hour, minute);
  if (!matches.length) throw new RomeTimeError('nonexistent');
  if (matches.length > 1 && !occurrence) throw new RomeTimeError('ambiguous');
  return matches[occurrence === 'later' ? matches.length - 1 : 0]!;
}

/** The Italian calendar day ('YYYY-MM-DD') of an instant. */
export function romeDate(iso: string | Date): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: COMPANY_TIME_ZONE }).format(new Date(iso));
}

/** Now in Italy as 'YYYY-MM-DDTHH:mm', the format of a datetime-local input. */
export function romeNowLocal(now = new Date()): string {
  const p = Object.fromEntries(parts.formatToParts(now).map((x) => [x.type, x.value]));
  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
}

/** 'YYYY-MM-DDTHH:mm' read as Italian time (a datetime-local value), as a UTC ISO instant. */
export function romeLocalTimeCandidates(local: string): string[] {
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(local)) throw new RomeTimeError('invalid');
  const [date, hm] = local.split('T');
  const [h, m] = hm!.split(':').map(Number);
  return romeWallTimeCandidates(date!, h!, m!);
}

export function romeLocalToIso(local: string, occurrence?: RomeOccurrence): string {
  const matches = romeLocalTimeCandidates(local);
  if (!matches.length) throw new RomeTimeError('nonexistent');
  if (matches.length > 1 && !occurrence) throw new RomeTimeError('ambiguous');
  return matches[occurrence === 'later' ? matches.length - 1 : 0]!;
}
